import { FilePlus2, FileText, LayoutTemplate, Palette } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router";

import { PublicFooter } from "@/components/layout/PublicFooter";
import { useSellerProfile } from "@/lib/db/hooks";
import { isInvoiceOnlyScope } from "@/lib/deployment/public-scope";
import {
  APP_BOTTOM_NAV_ITEMS,
  APP_TOP_NAV_ITEMS,
  isInvoiceAreaPath,
  isInvoiceDesignAreaPath,
} from "@/lib/navigation/app-ia";
import { useEntitlementState } from "@/lib/pricing/entitlement-context";
import { usePurchaseRestoreStatus } from "@/lib/pricing/purchase-verification-provider";

export function AppShell() {
  const profile = useSellerProfile();
  const entitlement = useEntitlementState();
  const purchaseRestoreStatus = usePurchaseRestoreStatus();
  const location = useLocation();
  const invoiceActive = isInvoiceAreaPath(location.pathname);
  const invoiceDesignActive = isInvoiceDesignAreaPath(location.pathname);
  const planLabel = entitlement.plan === "free" ? "Free plan" : planDisplayLabel(entitlement.plan);
  const restoreLabel = purchaseRestoreStatusLabel(purchaseRestoreStatus);
  const invoiceOnly = isInvoiceOnlyScope();

  if (profile === undefined) {
    return (
      <div className="min-h-dvh bg-canvas text-primary flex items-center justify-center">
        <p className="text-sm text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas text-primary">
      {/* Header (sticky, blur) */}
      <header className="sticky top-0 z-40 border-b border-border bg-canvas/80 backdrop-blur supports-[backdrop-filter]:bg-canvas/60">
        <div className="mx-auto flex h-14 max-w-[1760px] items-center justify-between px-4 md:px-6">
          <NavLink to="/app" className="font-semibold tracking-tight">
            Invoice Builder
          </NavLink>
          <nav className="hidden items-center gap-1 md:flex">
            {APP_TOP_NAV_ITEMS.map((item) => (
              <NavItem
                key={item.to}
                to={item.to}
                label={item.label}
                active={
                  item.to === "/app"
                    ? invoiceActive
                    : item.to === "/app/invoice-designs"
                      ? invoiceDesignActive
                      : undefined
                }
              />
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {!invoiceOnly && (
              <NavLink
                to="/pricing"
                className="rounded-md bg-elevated px-2.5 py-1 text-xs tabular-nums text-secondary transition-colors hover:bg-border"
                title="Basic document tools are free. Premium checkout is available only from pricing."
              >
                Pricing
              </NavLink>
            )}
            <span
              className="rounded-md bg-elevated px-2.5 py-1 text-xs tabular-nums text-tertiary"
              title="Current local access state"
            >
              {planLabel}
            </span>
            {restoreLabel ? (
              <span className="hidden text-xs text-tertiary sm:inline">{restoreLabel}</span>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1760px] flex-1 px-4 py-8 pb-24 md:px-6 md:pb-8">
        <Outlet />
      </main>

      <PublicFooter />

      <nav className="fixed right-0 bottom-0 left-0 z-50 border-t border-border bg-canvas pb-safe md:hidden">
        <div className="grid h-16 grid-cols-4">
          {APP_BOTTOM_NAV_ITEMS.map((item) => (
            <BottomTab
              key={item.to}
              to={item.to}
              icon={bottomTabIcon(item.label)}
              label={item.label}
              primary={item.label === "New"}
              active={item.to === "/app" ? invoiceActive : undefined}
            />
          ))}
        </div>
      </nav>
    </div>
  );
}

function planDisplayLabel(plan: "monthly" | "lifetime") {
  return plan === "monthly" ? "Monthly" : "Lifetime";
}

function purchaseRestoreStatusLabel(status: ReturnType<typeof usePurchaseRestoreStatus>) {
  if (status.state === "checking") return "Checking purchase";
  if (status.state === "restored") return "Purchase restored";
  if (status.state === "failed_closed" && status.reason === "active_browser_limit_reached") {
    return "Browser limit reached";
  }
  if (status.state === "failed_closed" && status.reason === "paypal_purchase_revoked") {
    return "Purchase inactive";
  }
  if (status.state === "failed_closed") return "Purchase restore issue";
  return null;
}

function bottomTabIcon(label: string) {
  if (label === "New") {
    return (
      <span className="-mt-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-fg">
        <FilePlus2 size={18} />
      </span>
    );
  }
  if (label === "Presets" || label === "Reusable Presets") {
    return <LayoutTemplate size={20} />;
  }
  if (label === "Customers") return <FileText size={20} />;
  if (label === "Items") return <LayoutTemplate size={20} />;
  if (label === "Brand Kit") return <Palette size={20} />;
  return <FileText size={20} />;
}

function NavItem({
  to,
  exact,
  label,
  active,
}: {
  to: string;
  exact?: boolean;
  label: string;
  active?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive, isPending }) =>
        `rounded-md px-3 py-1.5 text-sm transition-colors ${
          active || isActive || isPending
            ? "bg-elevated text-primary"
            : "text-secondary hover:bg-elevated hover:text-primary"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function BottomTab({
  to,
  exact,
  icon,
  label,
  primary,
  active,
}: {
  to: string;
  exact?: boolean;
  icon: React.ReactNode;
  label: string;
  primary?: boolean;
  active?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 text-xs transition-colors ${
          primary
            ? "text-secondary"
            : active || isActive
              ? "text-primary"
              : "text-tertiary hover:text-secondary"
        }`
      }
    >
      {icon}
      <span className="text-[10px]">{label}</span>
    </NavLink>
  );
}
